import React, { FC } from 'react';
import { useHistory, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Grid, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { FeedKey, GetGroupFollowersDocument, Group } from 'api';
import { PrivateGroupIcon, FollowToggle, GroupAvatar } from 'components';

import { useCurrentUser } from 'hooks';
import { isAdminUser } from 'helpers';

import Tabs from './Tabs';

interface IParams {
  group_id: string;
}

interface IProps {
  group: Group;
}

const useStyles = makeStyles(theme => ({
  block: {
    width: theme.spacing(2),
    height: theme.spacing(2),
    borderRadius: '50%',
  },
  button: {
    minWidth: 150,
  },
  container: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    paddingRight: theme.spacing(2),
  },
  editButton: {
    marginLeft: theme.spacing(2),
  },
}));

const DesktopBanner: FC<IProps> = ({ group }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const { group_id } = useParams<IParams>();
  const currentUser = useCurrentUser();
  const isAdmin = isAdminUser(currentUser);

  function renderTopRow() {
    return (
      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <GroupAvatar square group={group} size={68} />
        </Grid>
        <Grid item>
          <PrivateGroupIcon group={group} type="title" />
        </Grid>
        <Grid item xs zeroMinWidth>
          <Typography title={group.title} color="textPrimary" variant="h4" noWrap>
            {group.title}
          </Typography>
        </Grid>
        <Grid item>
          <Typography>{group.department_title}</Typography>
        </Grid>
        <Grid item>
          <Typography>{group.location_title}</Typography>
        </Grid>
        <FollowToggle
          source={group}
          type={FeedKey.Group}
          refetchQueries={[
            { query: GetGroupFollowersDocument, variables: { id: group.id, limit: 10 } },
          ]}>
          {({ isFollowing, onClick }) => {
            return (
              <Grid item>
                {!isFollowing && (
                  <Button
                    size="large"
                    className={classes.button}
                    color="primary"
                    variant="outlined"
                    onClick={onClick}>
                    {t('common:join')}
                  </Button>
                )}

                {isAdmin && (
                  <Button
                    size="large"
                    variant="outlined"
                    className={classes.editButton}
                    onClick={() => history.push(`/group/${group_id}/edit`)}>
                    {t('common:edit')}
                  </Button>
                )}
              </Grid>
            );
          }}
        </FollowToggle>
      </Grid>
    );
  }

  return (
    <Paper>
      <Grid className={classes.container} container alignItems="center">
        <Grid item xs>
          {renderTopRow()}
        </Grid>
      </Grid>
      <Grid container>
        <Grid item>
          <Tabs group={group} />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default DesktopBanner;
